import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Optional,
  Renderer2
} from '@angular/core';
import { NgControl } from '@angular/forms';
import nspell from 'nspell';

@Directive({
  selector: '[apptextareaSpellCheck]'
})
export class TextareaSpellcheckDirective implements OnInit {

  @Input() lang: 'en' | 'de' = 'en';
  private static dictionaryCache: Map<string, Promise<any>> = new Map();

  private spell!: any;
  private highlightDiv!: HTMLDivElement;
  private suggestionBox!: HTMLDivElement;
  private debounceTimer: any;
  private currentWordRange!: { start: number; end: number };
  private userTyped = false;
  private outerWrapper!: HTMLDivElement;

  constructor(
    private el: ElementRef<HTMLTextAreaElement>,
    private renderer: Renderer2,
    @Optional() private ngControl: NgControl
  ) { }

  async ngOnInit() {
    this.loadLanguage();
    await this.loadDictionary();
    this.disableBrowserSpellcheck();
    this.createHighlightLayer();
    this.createSuggestionBox();

    this.ngControl?.valueChanges?.subscribe(value => {
      if (!value) {
        this.highlightDiv.innerHTML = '';
        this.hideSuggestions();
      }
    });
  }
  private loadLanguage() {
    const stored = (localStorage.getItem('asagoDefaultUserLang') || 'en').toLowerCase();
    this.lang = stored === 'de' ? 'de' : 'en';
  }

private async loadDictionary() {
  const cacheKey = this.lang;

  // 👇 only fetch if not already fetching/fetched
  if (!TextareaSpellcheckDirective.dictionaryCache.has(cacheKey)) {
    const promise = Promise.all([
      fetch(`assets/dict/${this.lang}.aff`).then(r => r.text()),
      fetch(`assets/dict/${this.lang}.dic`).then(r => r.text())
    ]).then(([aff, dic]) => nspell({ aff, dic }));

    TextareaSpellcheckDirective.dictionaryCache.set(cacheKey, promise);
  }

  // 👇 all instances share the same spell checker
  this.spell = await TextareaSpellcheckDirective.dictionaryCache.get(cacheKey);
}
  private disableBrowserSpellcheck() {
    this.el.nativeElement.setAttribute('spellcheck', 'false');
  }

  @HostListener('input')
  onInput() {
    this.userTyped = true;
    this.syncScroll();
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.runSpellcheck(true);
      this.userTyped = false; // Reset after processing
    }, 300); // Increased debounce for better performance
  }

  @HostListener('blur')
  onBlur() {
    this.hideSuggestions();
    const text = this.el.nativeElement.value;
    this.highlightDiv.innerHTML = this.getHighlightedHTML(text, true);
  }

 

  private runSpellcheck(fromUser = false) {
    const text = this.el.nativeElement.value;
    if (!text.trim()) {
      this.highlightDiv.innerHTML = '';
      this.hideSuggestions();
      return;
    }
    this.highlightDiv.innerHTML = this.getHighlightedHTML(text);
    this.syncScroll();
    if (fromUser && this.userTyped) {
      this.detectWordForSuggestions(text);
    }
  }

  private createHighlightLayer() {
    const textarea = this.el.nativeElement;

    // OUTER container (allows suggestion box to escape)
    const outer = this.renderer.createElement('div');
    Object.assign(outer.style, {
      position: 'relative',
      width: '100%',
      overflow: 'visible'
    });
    textarea.parentElement!.insertBefore(outer, textarea);

    // INNER wrapper - needs extra space for underlines
    const wrapper = this.renderer.createElement('div');
    Object.assign(wrapper.style, {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      paddingTop: '3px', // Extra space for first line underlines
      paddingBottom: '3px' // Extra space for last line underlines
    });
    outer.appendChild(wrapper);
    wrapper.appendChild(textarea);

    const style = getComputedStyle(textarea);

    this.highlightDiv = this.renderer.createElement('div');
    Object.assign(this.highlightDiv.style, {
      position: 'absolute',
      top: '-3px', // Offset to align with textarea after wrapper padding
      left: '0',
      inset: '1px',
      right: '0',
      bottom: '-3px',
      pointerEvents: 'none',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      letterSpacing: style.letterSpacing,
      lineHeight: style.lineHeight === 'normal' ? '1.5' : style.lineHeight,
      boxSizing: 'border-box',
      color: 'transparent',
      width: '100%',
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
      paddingLeft: style.paddingLeft,
      paddingRight: style.paddingRight,
      border: style.border,
      overflow: 'hidden' // Prevent scrollbars on highlight div
    });

    // Adjust textarea to compensate for wrapper padding
    textarea.style.background = 'transparent';
    textarea.style.position = 'relative';
    textarea.style.zIndex = '1';
    textarea.style.marginTop = '-3px';
    textarea.style.marginBottom = '-3px';
    
    wrapper.appendChild(this.highlightDiv);
    this.outerWrapper = outer;
    textarea.addEventListener('scroll', () => {
      this.syncScroll();
    });

  }

  private getHighlightedHTML(text: string , force = false): string {
    const cursorPos = this.el.nativeElement.selectionStart ?? 0;
    const escaped = text.replace(/[&<>"']/g, m =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]!)
    );

    return escaped.replace(/\b[\p{L}']+\b/gu, (word, offset) => {
      const start = offset;
      const end = offset + word.length;

      // Skip word at cursor position
      if (!force && cursorPos >= start && cursorPos <= end) return word;
      
      
      // Check spelling
      return this.spell.correct(word) ? word : `<span class="spell-error">${word}</span>`;
    });
  }

  private createSuggestionBox() {
    this.suggestionBox = this.renderer.createElement('div');
    Object.assign(this.suggestionBox.style, {
      position: 'absolute',
      background: '#fff',
      border: '1px solid #ccc',
      fontSize: '13px',
      display: 'none',
      zIndex: '9999',
      boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
      borderRadius: '4px',
      minWidth: '120px',
      maxHeight: '200px',
      overflowY: 'auto'
    });
    document.body.append(this.suggestionBox);
  }

  private hideSuggestions() {
    this.suggestionBox.style.display = 'none';
  }

  private detectWordForSuggestions(text: string) {
    const textarea = this.el.nativeElement;
    const pos = textarea.selectionStart ?? 0;
    
    // Find word boundaries around cursor
    let start = pos;
    let end = pos;
    
    // Find start of word
    while (start > 0 && /[\p{L}']/u.test(text[start - 1])) {
      start--;
    }
    
    // Find end of word
    while (end < text.length && /[\p{L}']/u.test(text[end])) {
      end++;
    }
    
    const word = text.substring(start, end);
    
    // Only show suggestions if word is misspelled
    if (!word || word.length < 2 || this.spell.correct(word)) {
      return this.hideSuggestions();
    }
    
    this.currentWordRange = { start, end };
    this.showSuggestions(this.spell.suggest(word).slice(0, 5));
  }

  private showSuggestions(words: string[]) {
  if (!words.length) return this.hideSuggestions();

  this.suggestionBox.innerHTML = '';

  words.forEach(word => {
    const div = this.renderer.createElement('div');
    div.innerText = word;
    Object.assign(div.style, {
      padding: '6px 10px',
      cursor: 'pointer'
    });

    div.addEventListener('mousedown', e => {
      e.preventDefault();
      this.applySuggestion(word);
    });

    this.suggestionBox.appendChild(div);
  });

  const position = this.getWordPosition();
  this.suggestionBox.style.left = `${position.left}px`;
  this.suggestionBox.style.top = `${position.top}px`;
  this.suggestionBox.style.display = 'block';
}


  private applySuggestion(word: string) {
    const { start, end } = this.currentWordRange;
    const textarea = this.el.nativeElement;
    const value = textarea.value;
    const newValue = value.slice(0, start) + word + value.slice(end);
    
    // Update textarea value
    textarea.value = newValue;
    
    if (this.ngControl?.control) {
      this.ngControl.control.setValue(newValue);
    }
    
    const newCursorPos = start + word.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    this.hideSuggestions();
    this.userTyped = false;
    this.runSpellcheck(false);
    
    textarea.focus();
  }
   @HostListener('paste')
  onPaste() {
    this.userTyped = false;
    setTimeout(() => {
      this.syncScroll();
      this.runSpellcheck(false);
    }, 10);
  }
    @HostListener('scroll')
  onScroll() {
    this.syncScroll();
  }

  private syncScroll() {
    const textarea = this.el.nativeElement;
    this.highlightDiv.scrollTop = textarea.scrollTop;
    this.highlightDiv.scrollLeft = textarea.scrollLeft;
  }
  private getWordPosition(): { left: number; top: number } {
  const input = this.el.nativeElement;
  const { start } = this.currentWordRange;
  
  // Create a mirror div to calculate position
  const mirror = document.createElement('div');
  const style = getComputedStyle(input);
  
  Object.assign(mirror.style, {
    position: 'absolute',
    visibility: 'hidden',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    width: style.width,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight,
    padding: style.padding,
    border: style.border,
    boxSizing: style.boxSizing
  });
  
  // Text before cursor
  const textBeforeWord = input.value.substring(0, start);
  mirror.textContent = textBeforeWord;
  
  // Add a marker span at cursor position
  const marker = document.createElement('span');
  marker.textContent = '|';
  mirror.appendChild(marker);
  
  document.body.appendChild(mirror);
  
  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  
  document.body.removeChild(mirror);
  
  // Get input position
  const inputRect = input.getBoundingClientRect();
  
  // Calculate relative position
  const left = inputRect.left + (markerRect.left - mirrorRect.left) - input.scrollLeft;
  const top = inputRect.top + (markerRect.top - mirrorRect.top) + parseFloat(style.lineHeight || style.fontSize) - input.scrollTop + 2;
  
  return { left, top };
}
}